"use client";

import {useState, useEffect} from "react";
import NavLinkLogo from "./NavLinkLogo";

import {useSidebar} from "./nav/SidebarContext";
import ProfileBtn from "./ProfileBtn";
import {useSession} from "next-auth/react";
import LocaleSwitcher from "./LocaleSwitcher";
import LocaleLink from "./LocaleLink";

const Navbar = () => {
  const {
    open, // desktop left sidebar
    toggle, // toggle desktop sidebar
    mobileOpen, // mobile slide‑in panel
    toggleMobile // toggle mobile panel
  } = useSidebar();
  // const {data: session, status} = useSession();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const {data: session, status} = useSession();

  // Lock the body scroll when menu is open and prevent horizontal scroll
  useEffect(() => {
    document.body.classList.toggle("overflow-hidden", isMenuOpen);
    return () => document.body.classList.remove("overflow-hidden");
  }, [isMenuOpen]);

  const handleLinkClick = () => {
    if (isMenuOpen) {
      setIsMenuOpen(false);
    }
  };

  return (
    <header className={`fixed z-50 top-0 left-0 w-full transition-all bg-black duration-300  ${isMenuOpen ? "bg-black bg-opacity-90" : "bg-black"}`}>
      <div className="px-8">
        <nav className="flex items-center justify-between py-3 ">
          <div className="flex items-center">
            <NavLinkLogo />
            <button
              aria-label={open ? "Collapse sidebar" : "Expand sidebar"}
              onClick={toggle}
              className="ml-3 hidden md:inline-flex h-8 w-8 items-center text-gray-500 justify-center rounded hover:text-primary transition-colors">
              {open ? (
                /* Collapse icon – bar slides left on hover */
                <svg width="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" aria-hidden="true">
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M9.35719 3H14.6428C15.7266 3 16.6007 3 17.3086 3.05782C18.0375 3.11737 18.6777 3.24318 19.27 3.54497C20.2108 4.02433 20.9757 4.78924 21.455 5.73005C21.7568 6.32234 21.8826 6.96253 21.9422 7.69138C22 8.39925 22 9.27339 22 10.3572V13.6428C22 14.7266 22 15.6008 21.9422 16.3086C21.8826 17.0375 21.7568 17.6777 21.455 18.27C20.9757 19.2108 20.2108 19.9757 19.27 20.455C18.6777 20.7568 18.0375 20.8826 17.3086 20.9422C16.6008 21 15.7266 21 14.6428 21H9.35717C8.27339 21 7.39925 21 6.69138 20.9422C5.96253 20.8826 5.32234 20.7568 4.73005 20.455C3.78924 19.9757 3.02433 19.2108 2.54497 18.27C2.24318 17.6777 2.11737 17.0375 2.05782 16.3086C1.99998 15.6007 1.99999 14.7266 2 13.6428V10.3572C2 9.2734 2 8.39926 2.05782 7.69138C2.11737 6.96253 2.24318 6.32234 2.54497 5.73005C3.02433 4.78924 3.78924 4.02433 4.73005 3.54497C5.32234 3.24318 5.96253 3.11737 6.69138 3.05782C7.39926 3 8.27341 3 9.35719 3Z"
                    fill="currentColor"
                    opacity="0.3"
                  />
                  <rect x="9" y="5" width="2" height="14" rx="1" className="origin-center transition-transform duration-300 ease-in-out group-hover:-translate-x-1" fill="currentColor" />
                </svg>
              ) : (
                /* Expand icon – solid bar left */
                <svg width="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" aria-hidden="true">
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M9.35719 3H14.6428C15.7266 3 16.6007 3 17.3086 3.05782C18.0375 3.11737 18.6777 3.24318 19.27 3.54497C20.2108 4.02433 20.9757 4.78924 21.455 5.73005C21.7568 6.32234 21.8826 6.96253 21.9422 7.69138C22 8.39925 22 9.27339 22 10.3572V13.6428C22 14.7266 22 15.6008 21.9422 16.3086C21.8826 17.0375 21.7568 17.6777 21.455 18.27C20.9757 19.2108 20.2108 19.9757 19.27 20.455C18.6777 20.7568 18.0375 20.8826 17.3086 20.9422C16.6008 21 15.7266 21 14.6428 21H9.35717C8.27339 21 7.39925 21 6.69138 20.9422C5.96253 20.8826 5.32234 20.7568 4.73005 20.455C3.78924 19.9757 3.02433 19.2108 2.54497 18.27C2.24318 17.6777 2.11737 17.0375 2.05782 16.3086C2 15.6007 2 14.7266 2 13.6428V10.3572C2 9.2734 2 8.39926 2.05782 7.69138C2.11737 6.96253 2.24318 6.32234 2.54497 5.73005C3.02433 4.78924 3.78924 4.02433 4.73005 3.54497C5.32234 3.24318 5.96253 3.11737 6.69138 3.05782C7.39926 3 8.27341 3 9.35719 3Z"
                    fill="currentColor"
                    opacity="0.3"
                  />
                  <rect x="4" /* left-aligned bar */ y="5" width="2" height="14" rx="1" className="origin-center transition-transform duration-300 ease-in-out group-hover:-translate-x-1" fill="currentColor" />
                </svg>
              )}
            </button>
          </div>

          {/* Mobile Menu Toggle */}
          <button title="Navigation menu" className="md:hidden text-gray-500 focus:outline-none z-50" aria-controls="navbarNavDropdown" aria-expanded={mobileOpen} onClick={toggleMobile}>
            {/* <FontAwesomeIcon icon={isMenuOpen ? faTimes : faBars} className="h-6 w-6" /> */}
            {mobileOpen ? (
              /* Collapse icon – bar slides left on hover */
              <svg width="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" aria-hidden="true">
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M9.35719 3H14.6428C15.7266 3 16.6007 3 17.3086 3.05782C18.0375 3.11737 18.6777 3.24318 19.27 3.54497C20.2108 4.02433 20.9757 4.78924 21.455 5.73005C21.7568 6.32234 21.8826 6.96253 21.9422 7.69138C22 8.39925 22 9.27339 22 10.3572V13.6428C22 14.7266 22 15.6008 21.9422 16.3086C21.8826 17.0375 21.7568 17.6777 21.455 18.27C20.9757 19.2108 20.2108 19.9757 19.27 20.455C18.6777 20.7568 18.0375 20.8826 17.3086 20.9422C16.6008 21 15.7266 21 14.6428 21H9.35717C8.27339 21 7.39925 21 6.69138 20.9422C5.96253 20.8826 5.32234 20.7568 4.73005 20.455C3.78924 19.9757 3.02433 19.2108 2.54497 18.27C2.24318 17.6777 2.11737 17.0375 2.05782 16.3086C1.99998 15.6007 1.99999 14.7266 2 13.6428V10.3572C2 9.2734 2 8.39926 2.05782 7.69138C2.11737 6.96253 2.24318 6.32234 2.54497 5.73005C3.02433 4.78924 3.78924 4.02433 4.73005 3.54497C5.32234 3.24318 5.96253 3.11737 6.69138 3.05782C7.39926 3 8.27341 3 9.35719 3Z"
                  fill="currentColor"
                  opacity="0.3"
                />
                <rect x="9" y="5" width="2" height="14" rx="1" className="origin-center transition-transform duration-300 ease-in-out group-hover:-translate-x-1" fill="currentColor" />
              </svg>
            ) : (
              /* Expand icon – solid bar left */
              <svg width="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" aria-hidden="true">
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M9.35719 3H14.6428C15.7266 3 16.6007 3 17.3086 3.05782C18.0375 3.11737 18.6777 3.24318 19.27 3.54497C20.2108 4.02433 20.9757 4.78924 21.455 5.73005C21.7568 6.32234 21.8826 6.96253 21.9422 7.69138C22 8.39925 22 9.27339 22 10.3572V13.6428C22 14.7266 22 15.6008 21.9422 16.3086C21.8826 17.0375 21.7568 17.6777 21.455 18.27C20.9757 19.2108 20.2108 19.9757 19.27 20.455C18.6777 20.7568 18.0375 20.8826 17.3086 20.9422C16.6008 21 15.7266 21 14.6428 21H9.35717C8.27339 21 7.39925 21 6.69138 20.9422C5.96253 20.8826 5.32234 20.7568 4.73005 20.455C3.78924 19.9757 3.02433 19.2108 2.54497 18.27C2.24318 17.6777 2.11737 17.0375 2.05782 16.3086C2 15.6007 2 14.7266 2 13.6428V10.3572C2 9.2734 2 8.39926 2.05782 7.69138C2.11737 6.96253 2.24318 6.32234 2.54497 5.73005C3.02433 4.78924 3.78924 4.02433 4.73005 3.54497C5.32234 3.24318 5.96253 3.11737 6.69138 3.05782C7.39926 3 8.27341 3 9.35719 3Z"
                  fill="currentColor"
                  opacity="0.3"
                />
                <rect x="4" /* left-aligned bar */ y="5" width="2" height="14" rx="1" className="origin-center transition-transform duration-300 ease-in-out group-hover:-translate-x-1" fill="currentColor" />
              </svg>
            )}
          </button>

          {/* Fullscreen Overlay Menu */}
          <div className={` inset-0 hidden bg-black bg-opacity-80 transition-transform duration-300 ease-in-out md:static md:bg-transparent md:flex md:items-center md:space-x-6 `} id="navbarNavDropdown">
            <ul className={`flex flex-col md:flex-row items-center md:space-x-2 space-y-4 md:space-y-0 text-2xl  mt-16 md:mt-0 md:pb-0`}>
              {/* <li>
                <NavLink href="https://exchange.mozgii.com/" label="Exchange" onClick={handleLinkClick} />
              </li>
              <li>
                <NavLink href="https://unity.mozgii.com/" label="Unity" onClick={handleLinkClick} />
              </li>
              <li>
                <NavLink href="https://studio.mozgii.com/" label="Studio" onClick={handleLinkClick} />
              </li>
              <li>
                <NavLink href="https://music.mozgii.com/" label="Music" onClick={handleLinkClick} />
              </li>
              <li>
                <NavLink href="/mozg-unit-economy" label="Unit Economy" isActive={pathname === "/mozg-unit-economy"} onClick={handleLinkClick} />
              </li>

              <li>
                <NavLink href="/news-and-blog" label="News & Blog" isActive={pathname === "/news-and-blog"} onClick={handleLinkClick} />
              </li>
              <li>
                <NavLink href="https://unity.mozgii.com/pitch-deck" label="Investors & Pitch Deck" onClick={handleLinkClick} />
              </li> */}
              {/* <li>
                <svg className="w-14 h-12 animate-pulse text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 250 70">
                  <rect x="20" y="7" width="250" height="70" rx="0" />
                </svg>
              </li> */}
              <li>
                <span className="text-sm text-gray-500">beta 0.11.1</span>
              </li>
              <li>
                {status === "loading" ? (
                  <svg className="w-14 h-12 animate-pulse text-gray-300" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 320 110">
                    <rect x="20" y="7" width="320" height="110" rx="0" />
                  </svg>
                ) : !session?.user ? (
                  <LocaleLink href="/login" onClick={handleLinkClick} className=" text-white rounded-full bg-gray-800 hover:bg-black hover:text-gold   transition-all duration-300 px-6 py-2 text-sm">
                    Log In
                  </LocaleLink>
                ) : (
                  <ProfileBtn onClick={handleLinkClick} />
                )}
              </li>
              {/* 
              <li>
                <Link
                  href="https://unity.mozgii.com/"
                  target="_blank"
                  onClick={handleLinkClick}
                  className=" text-white rounded-full bg-gray-800 hover:bg-black hover:text-gold  transition-all duration-300 px-6 pt-3 pb-2 text-sm">
                  Log In
                </Link>
              </li> */}
              <li>
                <LocaleSwitcher
                  // Use the same styling as your Login trigger:
                  buttonClassName="text-white rounded-full bg-gray-800 hover:bg-black hover:text-gold transition-all duration-300 px-6 py-2 text-sm"
                />
              </li>
            </ul>
          </div>
        </nav>
      </div>
    </header>
  );
};

export default Navbar;
